// src/app/shared/services/chat.service.ts

import {
  Injectable,
  inject,
  PLATFORM_ID,
  OnDestroy,
} from '@angular/core';
import { HttpClient, HttpParams, HttpRequest, HttpEventType, HttpEvent } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import {
  BehaviorSubject,
  Subject,
  Subscription,
  Observable,
} from 'rxjs';
import { environment } from '../../environments/environment';
import { SocketService } from './socket.service';
import { AuthStateService } from '../states/auth.state';
import { CallService } from './call.service';
import {
  IConversation,
  IConversationListResponse,
  IMessage,
  IMessageListResponse,
  IApiResponse,
  ITypingPayload,
  IReadUpdatePayload,
  IDeletedPayload,
  IPresencePayload,
  IPresenceMap,
  ITypingUser,
  ISocketError,
  ISendMessagePayload,
  IReplyTo,
  ICreateGroupPayload,
  MessageType,
} from '../models/messaging.models';

@Injectable({ providedIn: 'root' })
export class ChatService implements OnDestroy {

  /** A send with no server confirmation after this long is shown as "not sent". */
  private static readonly SEND_TIMEOUT_MS = 20_000;

  private http       = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);
  private socket     = inject(SocketService);
  private authState  = inject(AuthStateService);
  private callSvc    = inject(CallService);

  private readonly base = `${environment.apiUrl}/messages`;
  private subs = new Subscription();

  // ── State ─────────────────────────────────────────────────────────────────

  /** Raw conversation list — ALL types; components filter by tab */
  readonly conversations$ = new BehaviorSubject<IConversation[]>([]);

  readonly messages$             = new BehaviorSubject<IMessage[]>([]);
  readonly activeConversation$   = new BehaviorSubject<IConversation | null>(null);
  readonly totalUnread$          = new BehaviorSubject<number>(0);
  readonly typingUsers$          = new BehaviorSubject<Map<string, ITypingUser[]>>(new Map());
  readonly presence$             = new BehaviorSubject<IPresenceMap>({});
  readonly loadingConversations$ = new BehaviorSubject<boolean>(false);
  readonly loadingMessages$      = new BehaviorSubject<boolean>(false);
  readonly hasMoreMessages$      = new BehaviorSubject<boolean>(false);
  readonly socketError$          = new Subject<ISocketError>();
  /** Emits when a group this user was in gets permanently deleted by an admin,
   *  so the component layer can toast + navigate away if it was open. */
  readonly groupDeleted$         = new Subject<{ conversationId: string; groupName: string | null }>();

  private oldestMessageId: string | null = null;
  private chatInitialised = false;

  // ── Init / Teardown ───────────────────────────────────────────────────────

  init(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Always reload conversations every time the chat screen is opened.
    // This fixes the "need to refresh" bug when navigating to the chat page.
    this._fetchAllConversations();

    // Socket registration must only happen once per service lifetime —
    // guard with chatInitialised to avoid duplicate event listeners.
    if (this.chatInitialised) return;
    this.chatInitialised = true;

    this.socket.connect();
    this._registerSocketListeners();
    this.callSvc.initSignalling();
  }

  teardown(): void {
    this._leaveActiveConversation();
    this.socket.disconnect();
    this.subs.unsubscribe();
    this.subs = new Subscription();
    this.chatInitialised = false;
    this.conversations$.next([]);
    this.messages$.next([]);
    this.activeConversation$.next(null);
    this.totalUnread$.next(0);
    this.typingUsers$.next(new Map());
    this.presence$.next({});
  }

  ngOnDestroy(): void { this.teardown(); }

  // ── Public API: conversations ─────────────────────────────────────────────

  /**
   * Reload ALL conversations from the server and replace the list.
   * Components filter by tab client-side so we never need a type param here.
   */
  loadConversations(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this._fetchAllConversations();
  }

  openDm(recipientId: string): Observable<IApiResponse<IConversation>> {
    return this.http.post<IApiResponse<IConversation>>(
      `${this.base}/conversations/dm`, { recipientId },
    );
  }

  createGroup(payload: ICreateGroupPayload): Observable<IApiResponse<IConversation>> {
    return this.http.post<IApiResponse<IConversation>>(
      `${this.base}/conversations/group`, payload,
    );
  }

  updateGroup(
    conversationId: string,
    payload: { groupName?: string; groupDescription?: string; image?: File | null },
  ): Observable<IApiResponse<IConversation>> {
    // Use multipart/form-data whenever an image file is included so the
    // backend can pick it up via multer (req.file); otherwise send plain JSON.
    if (payload.image) {
      const formData = new FormData();
      if (payload.groupName !== undefined) formData.append('groupName', payload.groupName);
      if (payload.groupDescription !== undefined) formData.append('groupDescription', payload.groupDescription);
      formData.append('image', payload.image);
      return this.http.patch<IApiResponse<IConversation>>(
        `${this.base}/conversations/group/${conversationId}`, formData,
      );
    }

    const { groupName, groupDescription } = payload;
    return this.http.patch<IApiResponse<IConversation>>(
      `${this.base}/conversations/group/${conversationId}`, { groupName, groupDescription },
    );
  }

  addGroupMembers(conversationId: string, memberIds: string[]): Observable<IApiResponse<IConversation>> {
    return this.http.post<IApiResponse<IConversation>>(
      `${this.base}/conversations/group/${conversationId}/members`, { memberIds },
    );
  }

  toggleMemberDisabled(conversationId: string, memberId: string, disable: boolean): Observable<IApiResponse<IConversation>> {
    return this.http.patch<IApiResponse<IConversation>>(
      `${this.base}/conversations/group/${conversationId}/members/${memberId}/disable`, { disable },
    );
  }

  toggleOnlyAdminsCanSend(conversationId: string, onlyAdmins: boolean): Observable<IApiResponse<IConversation>> {
    return this.http.patch<IApiResponse<IConversation>>(
      `${this.base}/conversations/group/${conversationId}/admin-only`, { onlyAdmins },
    );
  }

  /**
   * Upload a media file as a message.
   * - Immediately inserts an optimistic placeholder bubble with uploadProgress=0.
   * - Updates placeholder progress as bytes are sent (WhatsApp-style).
   * - Replaces placeholder with the real server message on completion.
   * - Falls back to a REST reload if the socket doesn't deliver message:new.
   */
  uploadMedia(
    conversationId: string,
    file: File,
    type: 'IMAGE' | 'VIDEO' | 'AUDIO' | 'FILE',
  ): Observable<{ progress: number; done: boolean }> {
    const me        = this.authState.getAuthSnapshot().user;
    const pendingId = `pending-media-${Date.now()}-${Math.random()}`;

    // Build a local preview URL for images so the bubble shows something immediately
    const previewUrl = (type === 'IMAGE' || type === 'VIDEO')
      ? URL.createObjectURL(file)
      : null;

    // Insert optimistic placeholder at bottom of messages list
    const placeholder: IMessage = {
      _id:            pendingId,
      conversationId,
      sender: {
        _id:          me?.id ?? me?._id ?? '',
        displayName:  me?.displayName ?? '',
        userName:     me?.userName ?? '',
        profileImage: me?.profileImage ?? null,
      },
      type,
      content:        file.name,
      attachments: previewUrl
        ? [{ mediaId: pendingId, url: previewUrl, mimeType: file.type, fileName: file.name, fileSize: file.size }]
        : [],
      readBy:         [],
      isEdited:       false,
      isDeleted:      false,
      createdAt:      new Date().toISOString(),
      pending:        true,
      pendingId,
      uploadProgress: 0,
    } as any;

    this.messages$.next([...this.messages$.value, placeholder]);
    this.shouldScrollToBottomOnUpload = true;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    return new Observable<{ progress: number; done: boolean }>(observer => {
      const req = new HttpRequest(
        'POST',
        `${this.base}/conversations/${conversationId}/messages/upload`,
        formData,
        { reportProgress: true },
      );

      const sub = this.http.request<IApiResponse<IMessage>>(req).subscribe({
        next: (event: HttpEvent<IApiResponse<IMessage>>) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            const progress = Math.round((event.loaded / event.total) * 100);
            // Update the placeholder progress in-place
            this.messages$.next(
              this.messages$.value.map(m =>
                (m as any).pendingId === pendingId
                  ? { ...m, uploadProgress: progress }
                  : m,
              ),
            );
            observer.next({ progress, done: false });
          } else if (event.type === HttpEventType.Response) {
            const body = event.body;
            if (body?.success && body.data) {
              // Server returned the saved message — replace placeholder.
              // (The message:new socket handler resolves the same placeholder
              // by pending-state matching, so whichever of these two arrives
              // first wins and the other becomes a no-op — no duplicate.)
              const serverMsg = body.data as unknown as IMessage;
              this.messages$.next(
                this.messages$.value.map(m =>
                  (m as any).pendingId === pendingId
                    ? { ...serverMsg, pending: false, uploadProgress: undefined }
                    : m,
                ),
              );
              // Revoke object URL to free memory
              if (previewUrl) URL.revokeObjectURL(previewUrl);
              this._bubbleConversation(conversationId, serverMsg);
            } else {
              // Response didn't include message data — remove placeholder, rely on socket
              this.messages$.next(
                this.messages$.value.filter(m => (m as any).pendingId !== pendingId),
              );
              if (previewUrl) URL.revokeObjectURL(previewUrl);
            }
            observer.next({ progress: 100, done: true });
            observer.complete();
          }
        },
        error: (err) => {
          // Remove placeholder on error
          this.messages$.next(
            this.messages$.value.filter(m => (m as any).pendingId !== pendingId),
          );
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          observer.error(err);
        },
      });

      return () => sub.unsubscribe();
    });
  }

  // internal flag used by upload to scroll after inserting placeholder
  shouldScrollToBottomOnUpload = false;

  /**
   * Upload up to 5 files as a SINGLE message with multiple attachments —
   * rendered as a grouped album on both sides, same as WhatsApp — instead
   * of one message per file.
   */
  uploadMediaGroup(
    conversationId: string,
    items: { file: File; type: 'IMAGE' | 'VIDEO' | 'AUDIO' | 'FILE'; previewUrl?: string }[],
  ): Observable<{ progress: number; done: boolean }> {
    const me        = this.authState.getAuthSnapshot().user;
    const pendingId = `pending-media-${Date.now()}-${Math.random()}`;

    // Local preview URLs for images/videos so the album shows something
    // immediately, before the upload finishes.
    const attachments = items.map((item, i) => {
      const previewUrl = item.previewUrl ?? ((item.type === 'IMAGE' || item.type === 'VIDEO')
        ? URL.createObjectURL(item.file)
        : null);
      return {
        mediaId:  `${pendingId}-${i}`,
        url:      previewUrl ?? '',
        // HEIC is converted server-side; mark the placeholder as an image so
        // it renders as one even when the browser reported no type.
        mimeType: item.type === 'IMAGE' && !item.file.type.startsWith('image/') ? 'image/webp' : item.file.type,
        fileName: item.file.name,
        fileSize: item.file.size,
      };
    });

    // Mixed photos/videos are still shown as one IMAGE-type album (matches
    // WhatsApp allowing photo+video in the same album); anything else mixed
    // falls back to a generic FILE-type message. Per-attachment rendering
    // still branches on each attachment's own mimeType regardless.
    const types      = new Set(items.map(i => i.type));
    const groupType: 'IMAGE' | 'VIDEO' | 'AUDIO' | 'FILE' =
      types.size === 1
        ? items[0]!.type
        : [...types].every(t => t === 'IMAGE' || t === 'VIDEO')
          ? 'IMAGE'
          : 'FILE';

    const placeholder: IMessage = {
      _id:            pendingId,
      conversationId,
      sender: {
        _id:          me?.id ?? me?._id ?? '',
        displayName:  me?.displayName ?? '',
        userName:     me?.userName ?? '',
        profileImage: me?.profileImage ?? null,
      },
      type:           groupType,
      content:        undefined,
      attachments,
      readBy:         [],
      isEdited:       false,
      isDeleted:      false,
      createdAt:      new Date().toISOString(),
      pending:        true,
      pendingId,
      uploadProgress: 0,
    } as any;

    this.messages$.next([...this.messages$.value, placeholder]);
    this.shouldScrollToBottomOnUpload = true;

    const formData = new FormData();
    items.forEach(item => formData.append('files', item.file));
    formData.append('type', groupType);

    return new Observable<{ progress: number; done: boolean }>(observer => {
      const req = new HttpRequest(
        'POST',
        `${this.base}/conversations/${conversationId}/messages/upload`,
        formData,
        { reportProgress: true },
      );

      const sub = this.http.request<IApiResponse<IMessage>>(req).subscribe({
        next: (event: HttpEvent<IApiResponse<IMessage>>) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            const progress = Math.round((event.loaded / event.total) * 100);
            this.messages$.next(
              this.messages$.value.map(m =>
                (m as any).pendingId === pendingId
                  ? { ...m, uploadProgress: progress }
                  : m,
              ),
            );
            observer.next({ progress, done: false });
          } else if (event.type === HttpEventType.Response) {
            const body = event.body;
            if (body?.success && body.data) {
              const serverMsg = body.data as unknown as IMessage;
              this.messages$.next(
                this.messages$.value.map(m =>
                  (m as any).pendingId === pendingId
                    ? { ...serverMsg, pending: false, uploadProgress: undefined }
                    : m,
                ),
              );
              attachments.forEach(a => { if (a.url) URL.revokeObjectURL(a.url); });
              this._bubbleConversation(conversationId, serverMsg);
            } else {
              this.messages$.next(
                this.messages$.value.filter(m => (m as any).pendingId !== pendingId),
              );
              attachments.forEach(a => { if (a.url) URL.revokeObjectURL(a.url); });
            }
            observer.next({ progress: 100, done: true });
            observer.complete();
          }
        },
        error: (err) => {
          this.messages$.next(
            this.messages$.value.filter(m => (m as any).pendingId !== pendingId),
          );
          attachments.forEach(a => { if (a.url) URL.revokeObjectURL(a.url); });
          observer.error(err);
        },
      });

      return () => sub.unsubscribe();
    });
  }

  removeGroupMember(conversationId: string, memberId: string): Observable<IApiResponse<IConversation>> {
    return this.http.delete<IApiResponse<IConversation>>(
      `${this.base}/conversations/group/${conversationId}/members/${memberId}`,
    );
  }

  acceptMessageRequest(conversationId: string): Observable<IApiResponse<IConversation>> {
    return this.http.patch<IApiResponse<IConversation>>(
      `${this.base}/conversations/${conversationId}/requests/accept`, {},
    );
  }

  declineMessageRequest(conversationId: string): Observable<IApiResponse<null>> {
    return this.http.patch<IApiResponse<null>>(
      `${this.base}/conversations/${conversationId}/requests/decline`, {},
    );
  }

  muteConversation(conversationId: string, mute: boolean): Observable<IApiResponse<null>> {
    return this.http.patch<IApiResponse<null>>(
      `${this.base}/conversations/${conversationId}/mute`, { mute },
    );
  }

  deleteConversation(conversationId: string): Observable<IApiResponse<null>> {
    return this.http.delete<IApiResponse<null>>(
      `${this.base}/conversations/${conversationId}`,
    );
  }

  /** Permanently delete a group for everyone (admin only). */
  deleteGroup(conversationId: string): Observable<IApiResponse<null>> {
    return this.http.delete<IApiResponse<null>>(
      `${this.base}/conversations/group/${conversationId}`,
    );
  }

  // ── Open / Close conversation ─────────────────────────────────────────────

  /**
   * Open a conversation.
   * If the conversation is not yet in the sidebar list (brand-new DM), add it.
   */
  openConversation(conversation: IConversation): void {
    const prev = this.activeConversation$.value;

    // Leave previous room if switching
    if (prev && prev._id !== conversation._id) {
      this._leaveActiveConversation();
    }

    // Same conversation already open — no reload needed
    if (prev && prev._id === conversation._id) return;

    this.activeConversation$.next(conversation);
    this.messages$.next([]);
    this.oldestMessageId = null;
    this.hasMoreMessages$.next(false);

    // Ensure the conversation is in the sidebar list so the active highlight works
    // and so _bubbleConversation can find it when a message arrives.
    this._ensureConversationInList(conversation);

    this.socket.emit('conversation:join', { conversationId: conversation._id });
    this._loadMessages(conversation._id);
    this._clearUnreadLocal(conversation._id);
  }

  closeConversation(): void {
    this._leaveActiveConversation();
    this.activeConversation$.next(null);
    this.messages$.next([]);
    this.oldestMessageId = null;
  }

  loadOlderMessages(): void {
    const conv = this.activeConversation$.value;
    if (!conv || this.loadingMessages$.value || !this.hasMoreMessages$.value) return;
    this._loadMessages(conv._id, this.oldestMessageId ?? undefined);
  }

  // ── Send / Edit / Delete ──────────────────────────────────────────────────

  sendMessage(payload: ISendMessagePayload): void {
    const me = this.authState.getAuthSnapshot().user;
    const pendingId = `pending-${Date.now()}-${Math.random()}`;

    const placeholder: IMessage = {
      _id:            pendingId,
      conversationId: payload.conversationId,
      sender: {
        _id:          me?.id ?? me?._id ?? '',
        displayName:  me?.displayName ?? '',
        userName:     me?.userName ?? '',
        profileImage: me?.profileImage ?? null,
      },
      type:        payload.type ?? 'TEXT',
      content:     payload.content,
      attachments: [],
      readBy:      [],
      isEdited:    false,
      isDeleted:   false,
      createdAt:   new Date().toISOString(),
      pending:     true,
      pendingId,
      // Show the reply quote straight away (it used to appear only once the
      // server echoed the message back).
      replyTo:     this._replySnapshot(payload.replyToId),
    };

    this.messages$.next([...this.messages$.value, placeholder]);

    // If the server never confirms (socket dropped, silent failure), stop
    // showing it as "sending" forever — mark it not sent so it can be retried.
    setTimeout(() => this.markSendFailed(pendingId, 'Not delivered'), ChatService.SEND_TIMEOUT_MS);

    this.socket.emit('message:send', {
      conversationId: payload.conversationId,
      type:           payload.type ?? 'TEXT',
      content:        payload.content,
      replyToId:      payload.replyToId,
      pendingId,
    });
  }

  /** Re-sends a message that failed (removes the failed copy first). */
  retryMessage(msg: IMessage): void {
    if (!msg.failed) return;
    this.messages$.next(this.messages$.value.filter(m => m !== msg && m._id !== msg._id));
    this.sendMessage({
      conversationId: msg.conversationId,
      type:           msg.type,
      content:        msg.content,
      replyToId:      msg.replyTo?.messageId,
    });
  }

  /**
   * A send the server rejected (e.g. a message request that no longer allows
   * replies) used to stay as a faded "pending" bubble forever, with no
   * explanation. Mark it failed instead so the UI can say so.
   */
  private markSendFailed(pendingId: string | null | undefined, reason: string): void {
    if (!pendingId) return;
    const list = this.messages$.value;
    const idx = list.findIndex(m => (m as any).pendingId === pendingId && m.pending);
    if (idx === -1) return;
    const updated = [...list];
    updated[idx] = { ...list[idx], pending: false, failed: true, failedReason: reason };
    this.messages$.next(updated);
  }

  /** Local copy of the quoted message, in the same shape the server stores. */
  private _replySnapshot(replyToId?: string): IReplyTo | null {
    if (!replyToId) return null;
    const original = this.messages$.value.find(m => m._id === replyToId);
    if (!original) return null;
    const isGif = original.type === 'GIF' || /^https?:\/\/media\d*\.giphy\.com\//.test(original.content ?? '');
    return {
      messageId:     original._id,
      senderId:      original.sender?._id,
      senderName:    original.sender?.displayName ?? '',
      contentSnip:   isGif ? '' : (original.content ?? '').slice(0, 100),
      hasAttachment: (original.attachments?.length ?? 0) > 0,
      gifUrl:        isGif ? original.content ?? null : null,
    };
  }

  sendMessageRest(
    conversationId: string,
    type: MessageType,
    content?: string,
    replyToId?: string,
  ): Observable<IApiResponse<IMessage>> {
    return this.http.post<IApiResponse<IMessage>>(
      `${this.base}/conversations/${conversationId}/messages`,
      { type, content, replyToId },
    );
  }

  editMessage(conversationId: string, messageId: string, content: string): void {
    this.socket.emit('message:edit', { conversationId, messageId, content });
  }

  deleteMessage(conversationId: string, messageId: string, deleteFor: 'me' | 'everyone'): void {
    this.socket.emit('message:delete', { conversationId, messageId, deleteFor });
  }

  markRead(conversationId: string): void {
    this.socket.emit('message:read', { conversationId });
    this._clearUnreadLocal(conversationId);
  }

  /**
   * Look up why a message can't be found locally — e.g. clicking a
   * reply-quote whose target the viewer soft-deleted ("for me"). See
   * getMessageStatus() in the backend's messaging.service.ts for the
   * full rationale.
   */
  getMessageStatus(
    conversationId: string,
    messageId:      string,
  ): Observable<IApiResponse<{ status: 'available' | 'deleted_for_viewer' | 'deleted_for_everyone' | 'not_found' }>> {
    return this.http.get<IApiResponse<{ status: 'available' | 'deleted_for_viewer' | 'deleted_for_everyone' | 'not_found' }>>(
      `${this.base}/conversations/${conversationId}/messages/${messageId}/status`,
    );
  }

  // ── Typing ────────────────────────────────────────────────────────────────

  private typingTimer: ReturnType<typeof setTimeout> | null = null;

  startTyping(conversationId: string): void {
    this.socket.emit('message:typing', { conversationId, isTyping: true });
    if (this.typingTimer) clearTimeout(this.typingTimer);
    this.typingTimer = setTimeout(() => this.stopTyping(conversationId), 3_000);
  }

  stopTyping(conversationId: string): void {
    if (this.typingTimer) { clearTimeout(this.typingTimer); this.typingTimer = null; }
    this.socket.emit('message:typing', { conversationId, isTyping: false });
  }

  // ── Presence ──────────────────────────────────────────────────────────────

  isOnline(userId: string): boolean {
    return this.presence$.value[userId]?.isOnline ?? false;
  }

  // ── Private: REST ─────────────────────────────────────────────────────────

  /**
   * Fetch ALL conversations (no type filter) and store in conversations$.
   * Client-side tab filtering is done in the component's getFilteredConversations().
   */
  private _fetchAllConversations(): void {
    this.loadingConversations$.next(true);

    const params = new HttpParams().set('page', '1').set('limit', '100');

    this.http
      .get<IApiResponse<IConversationListResponse>>(
        `${this.base}/conversations`,
        { params },
      )
      .subscribe({
        next: res => {
          if (res.success) {
            this.conversations$.next(res.data.conversations);
            this._recalcTotalUnread(res.data.conversations);
          }
          this.loadingConversations$.next(false);
        },
        error: () => this.loadingConversations$.next(false),
      });
  }

  private _loadMessages(conversationId: string, before?: string): void {
    this.loadingMessages$.next(true);

    let params = new HttpParams().set('limit', '50');
    if (before) params = params.set('before', before);

    this.http
      .get<IApiResponse<IMessageListResponse>>(
        `${this.base}/conversations/${conversationId}/messages`,
        { params },
      )
      .subscribe({
        next: res => {
          if (!res.success) return;
          const incoming = res.data.messages;
          if (before) {
            this.messages$.next([...incoming, ...this.messages$.value]);
          } else {
            this.messages$.next(incoming);
          }
          this.hasMoreMessages$.next(res.data.hasMore);
          if (incoming.length > 0) this.oldestMessageId = incoming[0]!._id;
          this.loadingMessages$.next(false);
        },
        error: () => this.loadingMessages$.next(false),
      });
  }

  // ── Private: Socket listeners ─────────────────────────────────────────────

  private _registerSocketListeners(): void {

    // message:new
    this.subs.add(
      this.socket.on<IMessage & { pendingId?: string }>('message:new').subscribe(message => {
        const activeId = this.activeConversation$.value?._id;
        const myId = this._myId();

        if (message.conversationId === activeId) {
          const isMine = message.sender?._id === myId;

          // Call records are created server-side and never have a placeholder,
          // so they must not be matched against a pending message of mine.
          if (isMine && message.type !== 'CALL') {
            // Resolve the optimistic placeholder for this message. Text sends
            // round-trip their pendingId through the socket, so match on that
            // first. Media uploads go through the REST endpoint instead, which
            // doesn't echo pendingId back — so fall back to the still-pending
            // placeholder of the same type/content (the file name) in this
            // conversation. Matching by state instead of arrival order means
            // it doesn't matter whether this socket event or the HTTP upload
            // response resolves the placeholder first — whichever arrives
            // first wins, and the second one becomes a no-op via alreadyExists.
            const list = this.messages$.value;
            let matchIndex = message.pendingId
              ? list.findIndex(m => (m as any).pendingId === message.pendingId)
              : -1;
            if (matchIndex === -1) {
              matchIndex = list.findIndex(m =>
                (m as any).pending &&
                m.sender?._id === myId &&
                m.type === message.type &&
                m.content === message.content,
              );
            }
            if (matchIndex === -1) {
              matchIndex = list.findIndex(m => (m as any).pending && m.sender?._id === myId);
            }

            if (matchIndex !== -1) {
              const updated = [...list];
              updated[matchIndex] = { ...message, pending: false };
              this.messages$.next(updated);
            } else {
              const alreadyExists = list.some(m => m._id === message._id);
              if (!alreadyExists) {
                this.messages$.next([...list, { ...message, pending: false }]);
              }
            }
          } else {
            // Message from someone else — append if not already present.
            const alreadyExists = this.messages$.value.some(m => m._id === message._id);
            if (!alreadyExists) {
              this.messages$.next([...this.messages$.value, { ...message, pending: false }]);
            }
            // Mark read since window is open
            this.markRead(message.conversationId);
          }
        }

        // Always bubble conversation to top with new last-message preview.
        // _bubbleConversation handles unread bump for background conversations.
        this._bubbleConversation(message.conversationId, message);
      }),
    );

    // message:typing
    this.subs.add(
      this.socket.on<ITypingPayload>('message:typing').subscribe(payload => {
        const { conversationId, userId, isTyping } = payload;
        if (userId === this._myId()) return;

        const map = new Map(this.typingUsers$.value);
        const current = map.get(conversationId) ?? [];

        if (isTyping) {
          if (!current.some(u => u.userId === userId)) {
            const conv = this.conversations$.value.find(c => c._id === conversationId);
            const participant = conv?.participants.find(p => p._id === userId);
            map.set(conversationId, [
              ...current,
              { conversationId, userId, displayName: participant?.displayName ?? 'Someone' },
            ]);
          }
        } else {
          map.set(conversationId, current.filter(u => u.userId !== userId));
        }
        this.typingUsers$.next(map);
      }),
    );

    // message:read:update
    this.subs.add(
      this.socket.on<IReadUpdatePayload>('message:read:update').subscribe(payload => {
        const { conversationId, userId, readAt } = payload;
        if (userId === this._myId()) return;
        if (conversationId !== this.activeConversation$.value?._id) return;

        this.messages$.next(
          this.messages$.value.map(m => {
            if (m.readBy.some(r => r.userId === userId)) return m;
            return { ...m, readBy: [...m.readBy, { userId, readAt }] };
          }),
        );
      }),
    );

    // message:edited
    this.subs.add(
      this.socket.on<IMessage>('message:edited').subscribe(updated => {
        if (updated.conversationId !== this.activeConversation$.value?._id) return;
        this.messages$.next(
          this.messages$.value.map(m => m._id === updated._id ? { ...m, ...updated } : m),
        );
      }),
    );

    // message:deleted
    this.subs.add(
      this.socket.on<IDeletedPayload>('message:deleted').subscribe(payload => {
        const { conversationId, messageId, deleteFor, userId } = payload;
        const myId = this._myId();
        if (conversationId !== this.activeConversation$.value?._id) return;

        if (deleteFor === 'everyone') {
          this.messages$.next(
            this.messages$.value.map(m =>
              m._id === messageId
                ? { ...m, isDeleted: true, content: undefined, attachments: [] }
                : m,
            ),
          );
        } else if (deleteFor === 'me' && userId === myId) {
          this.messages$.next(this.messages$.value.filter(m => m._id !== messageId));
        }
      }),
    );

    // presence:update
    this.subs.add(
      this.socket.on<IPresencePayload>('presence:update').subscribe(payload => {
        const current = { ...this.presence$.value };
        current[payload.userId] = { isOnline: payload.isOnline, lastSeen: payload.lastSeen };
        this.presence$.next(current);
      }),
    );

    // error — surface socket errors; retry conversation:join if it failed
    // (can happen when a brand-new DM arrives before the DB write propagates)
    this.subs.add(
      this.socket.on<ISocketError>('error').subscribe(err => {
        this.socketError$.next(err);

        if (err.event === 'message:send') this.markSendFailed(err.pendingId, err.message);

        // If joining the active conversation failed, retry once after 800 ms.
        // The backend already retries 500 ms on its side; this client-side
        // retry handles cases where even that wasn't enough.
        if (err.event === 'conversation:join') {
          const activeConv = this.activeConversation$.value;
          if (activeConv) {
            setTimeout(() => {
              this.socket.emit('conversation:join', { conversationId: activeConv._id });
            }, 800);
          }
        }
      }),
    );

    // conversation:new — emitted when a DM is created (openDm REST call).
    // Both participants receive this so their sidebars show the new chat
    // immediately without waiting for the first message.
    this.subs.add(
      this.socket.on<IConversation>('conversation:new').subscribe(conv => {
        const current = this.conversations$.value;
        const alreadyInList = current.some(c => c._id === conv._id);
        if (!alreadyInList) {
          const updated = [conv, ...current];
          this.conversations$.next(updated);
          this._recalcTotalUnread(updated);
        }

        // If this is the currently open conversation (sender's side),
        // make sure the socket room is joined. This handles the race where
        // conversation:join was emitted but failed because the DB write
        // hadn't propagated yet.
        const active = this.activeConversation$.value;
        if (active && active._id === conv._id) {
          this.socket.emit('conversation:join', { conversationId: conv._id });
        }
      }),
    );

    // conversation:update — sidebar sync when sender is in another tab/device
    this.subs.add(
      this.socket.on<{ conversationId: string; lastMessage: any; lastMessageAt: string }>('conversation:update')
        .subscribe(payload => {
          this._bubbleConversation(payload.conversationId, {
            _id:            payload.lastMessage?._id ?? '',
            conversationId: payload.conversationId,
            content:        payload.lastMessage?.content,
            type:           payload.lastMessage?.type ?? 'TEXT',
            sender:         payload.lastMessage?.sender ?? null,
            createdAt:      payload.lastMessageAt,
            attachments:    [],
            readBy:         [],
            isEdited:       false,
            isDeleted:      false,
          } as any);
        }),
    );

    // group:updated — emitted by the server after any admin action
    // (rename, mute member, toggle admin-only, add/remove member).
    // Update both the sidebar list AND activeConversation$ so the UI
    // reflects the change immediately without any reload.
    this.subs.add(
      this.socket.on<IConversation>('group:updated').subscribe(updated => {
        // Update conversations$ list
        const list = this.conversations$.value.map(c =>
          c._id === updated._id ? { ...c, ...updated } : c,
        );
        this.conversations$.next(list);

        // Update activeConversation$ if this is the open group
        const active = this.activeConversation$.value;
        if (active && active._id === updated._id) {
          this.activeConversation$.next({ ...active, ...updated });
        }
      }),
    );

    // conversation:deleted — a group admin permanently deleted the group.
    // Drop it from the sidebar for everyone, and close it if it was open.
    this.subs.add(
      this.socket.on<{ conversationId: string; groupName: string | null }>('conversation:deleted')
        .subscribe(({ conversationId, groupName }) => {
          this.conversations$.next(
            this.conversations$.value.filter(c => c._id !== conversationId),
          );

          const active = this.activeConversation$.value;
          if (active && active._id === conversationId) {
            this.closeConversation();
          }

          this.groupDeleted$.next({ conversationId, groupName });
        }),
    );
  }

  // ── Private: state helpers ────────────────────────────────────────────────

  private _myId(): string {
    const u = this.authState.getAuthSnapshot().user;
    return u?.id ?? u?._id ?? '';
  }

  private _leaveActiveConversation(): void {
    const conv = this.activeConversation$.value;
    if (conv) this.socket.emit('conversation:leave', { conversationId: conv._id });
  }

  /**
   * Ensure a conversation is present in the sidebar list.
   * Called when openConversation() is passed a conversation that may not
   * yet exist in the list (e.g. brand-new DM just created via openDm()).
   */
  private _ensureConversationInList(conversation: IConversation): void {
    const list = this.conversations$.value;
    const exists = list.some(c => c._id === conversation._id);
    if (!exists) {
      // Prepend it so it appears at the top immediately
      this.conversations$.next([conversation, ...list]);
      this._recalcTotalUnread([conversation, ...list]);
    }
  }

  /**
   * Move a conversation to the top of the list and update its lastMessage preview.
   * If the conversation isn't in the list yet, add it at the top
   * (can happen when the other user sends the first message in a new DM).
   */
  private _bubbleConversation(conversationId: string, message: IMessage): void {
    const list = [...this.conversations$.value];
    const index = list.findIndex(c => c._id === conversationId);
    const myId  = this._myId();
    const isActiveConv = this.activeConversation$.value?._id === conversationId;

    if (index === -1) {
      // Not in list — fetch this single conversation and prepend it.
      this.http
        .get<IApiResponse<IConversation>>(`${this.base}/conversations/${conversationId}`)
        .subscribe({
          next: res => {
            if (!res.success) return;
            const current = this.conversations$.value;
            const alreadyAdded = current.some(c => c._id === conversationId);
            if (!alreadyAdded) {
              const updated = [res.data, ...current];
              this.conversations$.next(updated);
              this._recalcTotalUnread(updated);
            }
          },
          error: () => {
            this._fetchAllConversations();
          },
        });
      return;
    }

    // Bump unread if the message is from someone else and the conv isn't open
    const isFromOther = message.sender?._id !== myId;
    const newUnread = (isFromOther && !isActiveConv)
      ? (list[index]!.unreadCount ?? 0) + 1
      : list[index]!.unreadCount ?? 0;

    // Conversation is already in the list — update and move to top
    const conv = {
      ...list[index]!,
      unreadCount: newUnread,
      lastMessage: {
        _id:             message._id,
        content:         message.content,
        type:            message.type,
        sender:          message.sender,
        createdAt:       message.createdAt,
        attachmentCount: message.attachments?.length ?? 0,
      },
      lastMessageAt: message.createdAt,
    };

    list.splice(index, 1);
    list.unshift(conv);
    this.conversations$.next(list);
    this._recalcTotalUnread(list);
  }

  private _bumpUnread(conversationId: string): void {
    const list = this.conversations$.value.map(c =>
      c._id === conversationId ? { ...c, unreadCount: (c.unreadCount ?? 0) + 1 } : c,
    );
    this.conversations$.next(list);
    this._recalcTotalUnread(list);
  }

  private _clearUnreadLocal(conversationId: string): void {
    const list = this.conversations$.value.map(c =>
      c._id === conversationId ? { ...c, unreadCount: 0 } : c,
    );
    this.conversations$.next(list);
    this._recalcTotalUnread(list);
  }

  private _recalcTotalUnread(list: IConversation[]): void {
    this.totalUnread$.next(list.reduce((sum, c) => sum + (c.unreadCount ?? 0), 0));
  }
}